<template>
  <div
    class="fixed top-0 left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <HeaderMini
      :name="$t('global.defaults.addTenancy')"
      :to="{ name: 'tenancies' }"
      :no-menu="true"
    ></HeaderMini>
    <HeaderShadow></HeaderShadow>

    <div class="p-4">
      <form @submit.prevent="addTenancy">
        <div class="flex pb-4">
          <div class="mr-2 w-1/2">
            <router-link to="/service-admin/tenancies">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
              ></ButtonSecondary>
            </router-link>
          </div>
          <div class="ml-2 w-1/2">
            <Button
              type="submit"
              :name="$t('global.defaults.addTenancy')"
              :disabled="
                !required(tenancy.name) ||
                !required(tenancy.region) ||
                !required(tenancy.language) ||
                !required(tenancy.contact.name) ||
                !validateEmail(tenancy.contact.email) ||
                !required(tenancy.api.version) ||
                !required(tenancy.api.username) ||
                !required(tenancy.api.password) ||
                !required(localAdmin.name) ||
                !validateEmail(localAdmin.email) ||
                !required(localAdmin.phone) ||
                !validatePhoneNumberTenant(
                  tenancy.contact.countryCode,
                  tenancy.contact.phone
                ) ||
                !validatePhoneNumberAdmin(
                  localAdmin.countryCode,
                  localAdmin.phone
                ) ||
                loading
              "
              :loading="loading"
            ></Button>
          </div>
        </div>

        <div
          class="bg-nucleus-white transition-all"
          :class="{ 'opacity-50': loading }"
        >
          <div class="flex items-center px-4 py-6 border-b">
            <div
              class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-tenancy"
            ></div>
            <div class="text-2xl">
              {{ $t("global.defaults.newTenancy") }}
            </div>
          </div>
          <div class="px-4 py-6 overflow-y-auto scroll-box">
            <Input
              id="tenant-name"
              v-model:value="tenancy.name"
              input-type="text"
              :label="$t('inputs.tenancyName.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.tenancyName.placeholder')"
              required
            />
            <Select
              id="tenant-region"
              v-model:value="tenancy.region"
              :label="$t('inputs.region.label')"
              :options="regions"
              required
            />
            <Select
              id="tenant-language"
              v-model:value="tenancy.language"
              :label="$t('inputs.language.label')"
              :options="languages"
              required
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.contactDetails") }}
              </div>
            </div>
            <Input
              id="contact-name"
              v-model:value="tenancy.contact.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              required
            />
            <Input
              id="contact-email"
              v-model:value="tenancy.contact.email"
              :update:value="validateEmail(tenancy.contact.email)"
              input-type="text"
              :label="$t('inputs.email.label')"
              :max-length="inputMaxLengths.email"
              :placeholder="$t('inputs.email.placeholder')"
              :error="error.tenancy.contact.email"
              required
            />
            <div class="flex relative">
              <div class="mr-2 w-1/4">
                <SelectCountry
                  id="contact-phone-code"
                  v-model:value="tenancy.contact.countryCode"
                  :label="$t('inputs.code.label')"
                  :options="countryCodes"
                  :options-common="countryCodesCommon"
                />
              </div>
              <div class="ml-2 w-3/4">
                <Input
                  id="contact-phone"
                  v-model:value="tenancy.contact.phone"
                  :update:value="
                    validatePhoneNumberTenant(
                      tenancy.contact.countryCode,
                      tenancy.contact.phone
                    )
                  "
                  input-type="text"
                  :label="$t('inputs.phone.label')"
                  icon=""
                  :max-length="inputMaxLengths.phone"
                  :placeholder="$t('inputs.phone.placeholder')"
                  :error="error.tenancy.contact.phone"
                />
              </div>
            </div>
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.sessionSettings") }}
              </div>
            </div>
            <Input
              id="max-broadcasts"
              v-model:value="tenancy.maxBroadcasts"
              input-type="number"
              :label="$t('inputs.maxBroadcasts.label')"
              :max-length="inputMaxLengths.maxBroadcasts"
              :placeholder="$t('inputs.maxBroadcasts.placeholder')"
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.apiDetails") }}
              </div>
            </div>
            <Select
              id="api-version"
              v-model:value="tenancy.api.version"
              :label="$t('inputs.apiVersion.label')"
              :options="apiVersionOptions"
              required
            />
            <Input
              id="api-username"
              v-model:value="tenancy.api.username"
              input-type="text"
              :label="$t('inputs.apiUsername.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.apiUsername.placeholder')"
              autocomplete="off"
              required
            />
            <Input
              id="api-password"
              v-model:value="tenancy.api.password"
              :input-type="passwordInputType"
              :label="$t('inputs.apiPassword.label')"
              :icon="passwordIcon"
              :max-length="inputMaxLengths.password"
              :placeholder="$t('inputs.apiPassword.placeholder')"
              autocomplete="new-password"
              required
              @updateInputType="
                (passwordInputType = $event.type), (passwordIcon = $event.icon)
              "
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.licenseDetails") }}
              </div>
            </div>
            <Input
              id="license"
              v-model:value="tenancy.license"
              input-type="text"
              :label="$t('inputs.license.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.license.placeholder')"
              autocomplete="off"
              required
            />

            <Input
              id="license-expiry"
              v-model:value="tenancy.licenseExpiry"
              input-type="date"
              :label="$t('inputs.licenseExpiry.label')"
              autocomplete="off"
              required
            />
            <Input
              id="initialCredits"
              v-model:value="tenancy.initialCredits"
              input-type="number"
              :label="$t('inputs.licenseCredits.label')"
              :max-length="inputMaxLengths.credits"
              :placeholder="$t('inputs.licenseCredits.placeholder')"
              autocomplete="off"
              required
            />
            <Input
              id="allowance-warning-threshold"
              v-model:value="tenancy.allowanceWarningThreshold"
              input-type="number"
              :label="$t('inputs.allowanceWarningThreshold.label')"
              :max-length="inputMaxLengths.allowanceWarningThreshold"
              :placeholder="$t('inputs.allowanceWarningThreshold.placeholder')"
            />
            <div class="border-b pt-2 mb-4">
              <div class="text-lg pb-2 text-nucleus-grey font-semibold">
                {{ $t("pages.tenancy.firstUser") }}
              </div>
            </div>
            <Input
              id="user-name"
              v-model:value="localAdmin.name"
              input-type="text"
              :label="$t('inputs.name.label')"
              :max-length="inputMaxLengths.name"
              :placeholder="$t('inputs.name.placeholder')"
              required
            />
            <Input
              id="user-email"
              v-model:value="localAdmin.email"
              :update:value="validateEmail(localAdmin.email)"
              input-type="text"
              :label="$t('inputs.email.label')"
              :max-length="inputMaxLengths.email"
              :placeholder="$t('inputs.email.placeholder')"
              :error="error.localAdmin.email"
              required
            />
            <div class="flex relative">
              <div class="mr-2 w-1/4">
                <SelectCountry
                  id="user-phone-code"
                  v-model:value="localAdmin.countryCode"
                  :label="$t('inputs.code.label')"
                  :options="countryCodes"
                  :options-common="countryCodesCommon"
                />
              </div>
              <div class="ml-2 w-3/4">
                <Input
                  id="user-phone"
                  v-model:value="localAdmin.phone"
                  :update:value="
                    validatePhoneNumberAdmin(
                      localAdmin.countryCode,
                      localAdmin.phone
                    )
                  "
                  input-type="text"
                  :label="$t('inputs.phone.label')"
                  icon=""
                  :max-length="inputMaxLengths.phone"
                  :placeholder="$t('inputs.phone.placeholder')"
                  :error="error.localAdmin.phone"
                  required
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      <ErrorBox
        v-if="errorHeading || errorContent"
        :heading="errorHeading"
        :content="errorContent"
        :fixed="errorFixed"
        @closeError="
          errorHeading = '';
          errorContent = '';
        "
      ></ErrorBox>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderMini from "@/components/global/HeaderMini.vue"; // @ is an alias to /src
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import InfoItem from "@/components/utils/InfoItem.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import SelectCountry from "@/components/utils/SelectCountry.vue"; // @ is an alias to /src
import HintLink from "@/components/utils/HintLink.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import router from "@/router";

import CountryCodesJson from "@/store/data/countryCodes.json"; // @ is an alias to /src
import CountryCodesCommonJson from "@/store/data/countryCodesCommon.json"; // @ is an alias to /src
import ApiVersionOptionsJson from "@/store/data/apiVersionOptions.json";
import RegionsJson from "@/store/data/regions.json";
import LanguagesJson from "@/store/data/languages.json";
import ErrorBox from "@/components/utils/ErrorBox.vue";

import parsePhoneNumber from "libphonenumber-js";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    HeaderMini,
    HeaderShadow,
    InfoItem,
    Input,
    Select,
    SelectCountry,
    HintLink,
    Button,
    ButtonSecondary,
    ErrorBox,
  },
  data() {
    return {
      apiVersionOptions: ApiVersionOptionsJson,
      countryCodes: CountryCodesJson,
      countryCodesCommon: CountryCodesCommonJson,
      regions: RegionsJson,
      tenancy: {
        name: "",
        contact: {
          name: "",
          email: "",
          countryCode: CountryCodesCommonJson[0].dial_code,
          phone: "",
        },
        api: {
          version: ApiVersionOptionsJson[0].id,
          username: "",
          password: "",
        },
        region: RegionsJson[0].id,
        language: LanguagesJson[0].id,
        license: "",
        licenseExpiry: "",
        initialCredits: "0",
      },
      localAdmin: {
        name: "",
        email: "",
        countryCode: CountryCodesCommonJson[0].dial_code,
        phone: "",
        role: "LOCAL_ADMIN",
        tenantId: "",
      },
      error: {
        tenancy: {
          contact: {
            email: "",
            phone: "",
          },
        },
        localAdmin: {
          email: "",
          phone: "",
        },
        initialCredits: 0,
        licenseExpiry: 0,
      },
      correctNumberTenant: "",
      correctNumberAdmin: "",
      passwordInputType: "password",
      passwordIcon: "show",
      loading: false,
      errorHeading: "",
      errorContent: "",
      errorFixed: true,
      inputMaxLengths: InputMaxLengths[0],
    };
  },
  computed: {
    languages() {
      return LanguagesJson.filter((language) => {
        const region =
          this.regions.find(
            (v: any) => v.id.toString() === this.tenancy.region
          ) || this.regions[0];
        return region.languages?.includes(language.locale);
      });
    },
  },
  watch: {
    languages(newLanguages) {
      // Change tenancy language if selected language no longer supported
      if (
        newLanguages.filter(
          (language: any) => language.locale === this.tenancy.language
        ).length === 0
      ) {
        this.tenancy.language = newLanguages[0].id;
      }
    },
  },
  methods: {
    addTenancy() {
      this.loading = true;

      const isoDate = new Date(this.tenancy.licenseExpiry).toISOString();

      console.log("iso Date", isoDate);

      const data = {
        name: this.tenancy.name,
        contact: {
          countryCode: this.tenancy.contact.countryCode,
          email: this.tenancy.contact.email,
          name: this.tenancy.contact.name,
          phone: this.correctNumberTenant,
        },
        region: this.tenancy.region,
        language: this.tenancy.language,
        api: this.tenancy.api,
        license: this.tenancy.license,
        licenseExpiry: isoDate,
        initialCredits: parseInt(this.tenancy.initialCredits),
        maxBroadcasts: parseInt(this.tenancy.maxBroadcasts),
        allowanceWarningThreshold: parseInt(
          this.tenancy.allowanceWarningThreshold
        ),
      };

      this.$store
        .dispatch("addTenancy", data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response.data) {
            this.$store.dispatch("changeUpdate", "tenancy");
            this.localAdmin.tenantId = response.data.tenantId;
            this.addLocalUser();
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    addLocalUser() {
      const data = {
        name: this.localAdmin.name,
        email: this.localAdmin.email,
        phone: this.correctNumberAdmin,
        role: this.localAdmin.role,
        tenantId: this.localAdmin.tenantId,
      };
      this.$store
        .dispatch("addLocalUser", data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          if (response) {
            console.log("add local admin", response);
            this.loading = false;
            router.push({ name: "tenancies" });
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    validateEmail(email: string) {
      const mailformat =
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

      if (this.required(email)) {
        if (!email.match(mailformat)) {
          this.error.email = this.$t("inputs.email.errorInvalid");
          return false;
        } else {
          this.error.email = "";
          return true;
        }
      } else {
        return false;
      }
    },
    // Refactor this!
    validatePhoneNumberTenant(code: any, phone: any) {
      const phoneNumberTenant = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumberTenant?.isValid()) {
          this.error.tenancy.contact.phone = "";
          this.correctNumberTenant = phoneNumberTenant.number;
          return true;
        } else {
          this.error.tenancy.contact.phone = this.$t(
            "inputs.phone.errorInvalid"
          );
          return false;
        }
      } else {
        return false;
      }
    },
    validatePhoneNumberAdmin(code: any, phone: any) {
      const phoneNumber = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumber?.isValid()) {
          this.error.localAdmin.phone = "";
          this.correctNumberAdmin = phoneNumber.number;
          return true;
        } else {
          this.error.localAdmin.phone = this.$t("inputs.phone.errorInvalid");
          return false;
        }
      } else {
        return false;
      }
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
  },
})
export default class TenancyAdd extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
